import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import React, { useEffect, useRef, useState } from 'react';
import { NoData } from '../MarketEffect.styles';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import {
	ChartWrapper,
	LoadingContainer,
	PriceIndexWrapper,
	SpendIndexWrapper,
} from './Charts.styles';

const priceChartInitialOptions = {
	chart: {
		type: 'line',
		height: '30%',
	},
	exporting: {
		enabled: false,
		allowHTML: true,
	},
	credits: {
		enabled: false,
	},
	title: {
		text: '',
	},
	legend: {
		align: 'center',
		padding: 0,
	},
	xAxis: {
		title: {
			text: 'Year and Month',
		},
		type: 'datetime',
		crosshair: true,
		lineColor: '#000000',
		labels: {
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
		gridLineWidth: 0,
	},
	yAxis: {
		title: {
			text: 'Weight Index',
		},
		gridLineWidth: 0,
		labels: {
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
		lineColor: '#000000',
		lineWidth: 1,
	},
	tooltip: {
		shared: true,
		useHTML: true,
	},
	plotOptions: {
		column: {
			pointPadding: 0.2,
			borderWidth: 0,
		},
	},
	colors: ['#032E45', '#448EB4', '#45B9F7', '#AFE1FC'],
};

const spendChartInitialOptions = {
	chart: {
		type: 'line',
		height: '30%',
	},
	exporting: {
		enabled: false,
		allowHTML: true,
	},
	credits: {
		enabled: false,
	},
	title: {
		text: '',
	},
	legend: {
		align: 'center',
		padding: 0,
	},
	xAxis: {
		title: {
			text: 'Year and Month',
		},
		type: 'datetime',
		crosshair: true,
		lineColor: '#000000',
		labels: {
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
		gridLineWidth: 0,
	},
	yAxis: {
		title: {
			text: 'Spend Index',
		},
		gridLineWidth: 0,
		labels: {
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
		lineColor: '#000000',
		lineWidth: 1,
	},
	tooltip: {
		shared: true,
		useHTML: true,
	},
	plotOptions: {
		column: {
			pointPadding: 0.2,
			borderWidth: 0,
		},
	},
	colors: ['#032E45', '#448EB4', '#45B9F7', '#AFE1FC'],
};

const Monthly = ({ chartData, isChartLoading }) => {
	const [data, setData] = useState([]);
	const [priceIndexChartOptions, setPriceIndexChartOptions] = useState({});
	const [spendIndexChartOptions, setSpendIndexChartOptions] = useState({});
	const priceIndexRefMonthly = useRef();
	const spendIndexRefMonthly = useRef();

	useEffect(
		() => {
			if (chartData.length > 0) {
				const {
					marketAnalysisPriceIndexChartData,
					marketAnalysisSpendIndexChartData,
				} = getSpendAndPriceDataForChart(chartData);
				setPriceIndexChartOptions({
					...priceChartInitialOptions,
					...{ series: marketAnalysisPriceIndexChartData },
				});
				setSpendIndexChartOptions({
					...spendChartInitialOptions,
					...{ series: marketAnalysisSpendIndexChartData },
				});
				setData(chartData);
			}
		}, // eslint-disable-next-line
		[chartData]
	);

	const sortByYearAndMonth = data => {
		return data.sort((a, b) => {
			if (a.pO_Year === b.pO_Year) {
				return a.pO_Month - b.pO_Month;
			}
			return a.pO_Year - b.pO_Year;
		});
	};

	const getSpendAndPriceDataForChart = data => {
		const sortedResult = sortByYearAndMonth(data);
		let result = {
			marketAnalysisPriceIndexChartData: [],
			marketAnalysisSpendIndexChartData: [],
		};
		result.marketAnalysisPriceIndexChartData = sortedResult.reduce(
			(acc, item) => {
				const existingSupplier = acc.find(s => s.name === item.supplier);
				const xValue = `${item.pO_Year}-${String(item.pO_Month).padStart(
					2,
					'0'
				) + '-01'}`;
				const yValue = item.total_price_index;

				if (existingSupplier) {
					existingSupplier.data.push([new Date(xValue).getTime(), yValue]);
				} else {
					acc.push({
						type: 'line',
						name: item.supplier,
						data: [[new Date(xValue).getTime(), yValue]],
					});
				}

				return acc;
			},
			[]
		);

		result.marketAnalysisSpendIndexChartData = sortedResult.reduce(
			(acc, item) => {
				const existingSupplier = acc.find(s => s.name === item.supplier);
				const xValue = `${item.pO_Year}-${String(item.pO_Month).padStart(
					2,
					'0'
				) + '-01'}`;
				const yValue = item.total_spend_index;

				if (existingSupplier) {
					existingSupplier.data.push([new Date(xValue).getTime(), yValue]);
				} else {
					acc.push({
						type: 'line',
						name: item.supplier,
						data: [[new Date(xValue).getTime(), yValue]],
					});
				}

				return acc;
			},
			[]
		);
		return result;
	};

	const Loader = () => (
		<LoadingContainer>
			<ApLoaderDefault loaderText={() => <ApLoaderText />} />
		</LoadingContainer>
	);

	return (
		<div>
			{isChartLoading ? (
				<Loader />
			) : (
				<>
					{data.length === 0 ? (
						<NoData>No data available.</NoData>
					) : (
						<ChartWrapper>
							<PriceIndexWrapper>
								<HighchartsReact
									highcharts={Highcharts}
									options={priceIndexChartOptions}
									ref={priceIndexRefMonthly}
								/>
							</PriceIndexWrapper>
							<SpendIndexWrapper>
								<div>
									<HighchartsReact
										highcharts={Highcharts}
										options={spendIndexChartOptions}
										ref={spendIndexRefMonthly}
									/>
								</div>
							</SpendIndexWrapper>
						</ChartWrapper>
					)}
				</>
			)}
		</div>
	);
};

export default React.memo(Monthly);
